import React, {useRef} from 'react';
import {FlatList, View} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {makeStyles} from '@rneui/themed';
import {observer} from 'mobx-react';

import {DEFAULT_CAROUSEL_NUM_ITEMS} from '../../../constants/GlobalConstants';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {useStores} from '../../../stores/use-stores';
import {OffersItemWithMflId} from '../../../transport/types';
import { brandRewardsCopy } from '../../../utils/rewards';
import Text from '../../themed/Text';
import TitleLine from '../TitleLine';

import ExclusiveOffersTileCompact from './ExclusiveOffersTileCompact';
import ExclusiveOffersTileLarge from './ExclusiveOffersTileLarge';

type ExclusiveOffersProps = {
  variant?: 'cart';
  numItems?: number;
  onSeeAllPress?: () => void;
};

function ExclusiveOffers({
  onSeeAllPress,
  variant,
  numItems = DEFAULT_CAROUSEL_NUM_ITEMS,
}: ExclusiveOffersProps) {
  const {offersStore, cartStore} = useStores();
  const {isWebDesktopScreen} = useWebScreenDimensions();

  const renderItem = createRenderItem(variant);

  const styles = useStyles({variant});

  const data = offersStore.data?.slice(0, numItems) || [];

  /**
   * Activated deal should appear first
   */
  const dataActivatedFirst = data.sort((a, b) => {
    if (cartStore.activatedDeals?.includes(a.mflId)) {
      return -1;
    }
    if (cartStore.activatedDeals?.includes(b.mflId)) {
      return 1;
    }
    return 0;
  });

  const flatListRef = useRef<FlatList>(null);

  useFocusEffect(
    React.useCallback(() => {
      // Code here will run when the screen is focused
      if (flatListRef.current) {
        if (dataActivatedFirst.length > 0) {
          flatListRef.current?.scrollToIndex({index: 0, animated: false});
        }
      }

      return () => {
        // Cleanup code here will run when the screen is unfocused
      };
    }, [dataActivatedFirst.length]),
  );

  return (
    <View>
      <TitleLine title={`${brandRewardsCopy.exclusiveOffersTitle.toUpperCase()}`} onSeeAllPress={onSeeAllPress} />
      {variant === 'cart' && (
        <Text style={{marginBottom: 16}} color="black">
          Choose one of the offers below
        </Text>
      )}
      <FlatList
        ref={flatListRef}
        style={styles.flatlistStyle}
        horizontal
        data={dataActivatedFirst}
        renderItem={renderItem}
        ItemSeparatorComponent={Separator}
        showsHorizontalScrollIndicator={isWebDesktopScreen}
        contentContainerStyle={{
          maxWidth: dataActivatedFirst.length === 0 ? '100%' : undefined,
        }}
        ListEmptyComponent={
          <Text>
            There are no current {brandRewardsCopy.exclusiveOffersTitle}. Please check back later.
          </Text>
        }
      />
    </View>
  );
}

const useStyles = makeStyles((theme, {variant}: ExclusiveOffersProps) => {
  /**
   * cart variant needs negative margin to make up bottom margin for shadow
   */
  return {
    flatlistStyle: variant === 'cart' ? {marginBottom: -16} : {},
  };
});

function Separator() {
  return <View style={{width: 8, height: 8}} />;
}

const createRenderItem =
  (variant?: 'cart') =>
  ({item}: {item: OffersItemWithMflId}) => {
    return variant === 'cart' ? (
      <ExclusiveOffersTileCompact {...item} />
    ) : (
      <ExclusiveOffersTileLarge {...item} />
    );
  };

export default observer(ExclusiveOffers);
